'use client'

/**
 * Single source for GET /api/admin/impersonate/status.
 *
 * Five independent callers used to fetch this on mount with no cache and no
 * dedup — two hooks (use-effective-identity, use-effective-rbac), two
 * mission-rail components, and the globally-mounted ImpersonationBar. A single
 * page load emitted 5-8 calls and every client-side navigation that remounted
 * the rail repeated them; AFD logs counted 4,307 calls in 7 days. Each one runs
 * `await auth()` + `resolveEffectiveUser(session)` server-side.
 *
 * Both impersonation transitions hard-navigate (window.location.href), so
 * module state is wiped on start/stop anyway — the TTL only bounds staleness
 * within a single page lifetime. `invalidate()` is exported for callers that
 * change impersonation without a navigation.
 */

export interface ImpersonateStatus {
  isAdmin?: boolean
  active?: boolean
  targetEmail?: string
  targetId?: string
  realEmail?: string
  targetName?: string
  targetJobTitle?: string | null
  targetDepartment?: string | null
  effectiveCaps?: string[]
  effectiveRole?: string | null
  effectiveRoles?: string[]
  /** True when `active` reflects a "view as role" preview (Story #24925),
   * not a real-user impersonation -- frontend-only, see impersonate.ts's
   * IMPERSONATE_ROLE_PREVIEW_COOKIE docstring. */
  isRolePreview?: boolean
  previewedRole?: string
}

export interface ImpersonateStatusResult {
  /**
   * False when the request failed or returned non-2xx — as opposed to
   * succeeding and reporting "not impersonating", which is `ok: true` with
   * `status.active` falsy.
   *
   * Callers currently treat both cases the same (no override), which means a
   * failed status check silently restores the admin's own caps. That fail-open
   * is tracked separately; this flag is what a fix needs to distinguish them,
   * and it exists so the fix lands in one place instead of five.
   */
  ok: boolean
  status: ImpersonateStatus | null
}

const TTL_MS = 30_000

let cached: { at: number; result: ImpersonateStatusResult } | null = null
let inFlight: Promise<ImpersonateStatusResult> | null = null

export function invalidateImpersonateStatus(): void {
  cached = null
  inFlight = null
}

export async function fetchImpersonateStatus(
  opts?: { force?: boolean },
): Promise<ImpersonateStatusResult> {
  if (!opts?.force) {
    if (cached && Date.now() - cached.at < TTL_MS) return cached.result
    // Concurrent mounts in the same tick share one request.
    if (inFlight) return inFlight
  }

  const request = (async (): Promise<ImpersonateStatusResult> => {
    try {
      const resp = await fetch('/api/admin/impersonate/status')
      if (!resp.ok) return { ok: false, status: null }
      return { ok: true, status: (await resp.json()) as ImpersonateStatus }
    } catch {
      return { ok: false, status: null }
    }
  })()

  inFlight = request
  try {
    const result = await request
    // Never cache a failure — a transient blip should not stick for the TTL.
    cached = result.ok ? { at: Date.now(), result } : null
    return result
  } finally {
    if (inFlight === request) inFlight = null
  }
}
